import { getCountryName, SupportedLanguage } from './countries'

/**
 * Address parts for structured address storage
 */
export interface AddressParts {
  street?: string
  city?: string
  postalCode?: string
  countryCode?: string // ISO 3166-1 alpha-3
}

/**
 * Format address parts into a single display string
 */
export function formatAddress(address: AddressParts, lang: SupportedLanguage = 'en'): string {
  const parts: string[] = []

  if (address.street) {
    parts.push(address.street)
  }

  // Combine city and postal code
  const cityPostal: string[] = []
  if (address.city) {
    cityPostal.push(address.city)
  }
  if (address.postalCode) {
    cityPostal.push(address.postalCode)
  }
  if (cityPostal.length > 0) {
    parts.push(cityPostal.join(' '))
  }

  // Add country name
  if (address.countryCode) {
    const countryName = getCountryName(address.countryCode, lang)
    if (countryName) {
      parts.push(countryName)
    }
  }

  return parts.join(', ')
}

/**
 * Parse a single address string into parts (best effort for backward compatibility)
 * This is a simple parser that attempts to extract components from a legacy address field
 */
export function parseAddress(fullAddress: string): AddressParts {
  if (!fullAddress) {
    return {}
  }

  // Simple approach: treat the whole string as street address
  // More sophisticated parsing could be added if needed
  return {
    street: fullAddress.trim(),
  }
}

/**
 * Check if address has any content
 */
export function hasAddress(address: AddressParts | undefined | null): boolean {
  if (!address) return false
  return !!(address.street || address.city || address.postalCode || address.countryCode)
}

/**
 * Format address for invoice display (multi-line format)
 */
export function formatAddressMultiline(
  address: AddressParts,
  lang: SupportedLanguage = 'en'
): string[] {
  const lines: string[] = []

  if (address.street) {
    lines.push(address.street)
  }

  // Combine city and postal code on one line
  const cityPostal: string[] = []
  if (address.postalCode) {
    cityPostal.push(address.postalCode)
  }
  if (address.city) {
    cityPostal.push(address.city)
  }
  if (cityPostal.length > 0) {
    lines.push(cityPostal.join(' '))
  }

  // Country on its own line
  if (address.countryCode) {
    const countryName = getCountryName(address.countryCode, lang)
    if (countryName) {
      lines.push(countryName)
    }
  }

  return lines
}

/**
 * Merge legacy address field with structured address
 * If structured fields are empty, falls back to legacy address
 */
export function mergeAddressFields(
  structured: AddressParts,
  legacyAddress?: string
): AddressParts {
  // If we have structured address, use it
  if (hasAddress(structured)) {
    return structured
  }

  // Fall back to parsing legacy address
  if (legacyAddress) {
    return parseAddress(legacyAddress)
  }

  return {}
}
